import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { Ripple } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { TooltipModule } from 'primeng/tooltip';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { MenuItem } from '@/layouts/sidebar/menu.model';
import { MENU } from '@/layouts/sidebar/menu';
import { filter, merge, of, switchMap } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { bootstrapArchive } from '@ng-icons/bootstrap-icons';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { DataRequestService } from '@/features/data-requests/data-request.service';
import { DataRequestStatus } from '@shared/models/data-requests';
import { map, skip } from 'rxjs/operators';
import { ModuleGroup } from '@shared/models/module-group.enum';
import { RouteAccessService } from '@core/services';
import { Permission } from '@shared/models';
import { menuRequirement } from '@core/permissions/route-permission';
import { PersonalizationService } from '@shared/services/personalization.service';
import { TranslatePipe } from '@ngx-translate/core';
import { LayoutService } from '@/shared/services/ui';

@Component({
  selector: 'cis-sidebar',
  imports: [Ripple, StyleClassModule, RouterLink, RouterLinkActive, NgIcon, BadgeModule, TranslatePipe, ButtonModule, TooltipModule],
  viewProviders: [provideIcons({ bootstrapArchive })],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent {
  private readonly personalizationService = inject(PersonalizationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly dataRequestService = inject(DataRequestService);
  private readonly layoutService = inject(LayoutService);
  private readonly routeAccess = inject(RouteAccessService);

  router = inject(Router);

  isCondensed = computed(() => !this.layoutService.sidebarVisible());

  constructor() {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.cdRef.detectChanges();
      });
  }

  dataRequestsCount = toSignal(
    merge(this.dataRequestService.refresher$, this.personalizationService.profiles$).pipe(
      skip(1),
      switchMap(() => {
        if (this.routeAccess.isAllowed({ group: ModuleGroup.DataRequest, permission: Permission.Read })) {
          return this.dataRequestService
            .getByQuery({
              pageSize: 0,
              pageNumber: 1,
              searchTerm: '',
              statusIds: [
                DataRequestStatus.CorrectionRequired,
                DataRequestStatus.InReview,
                DataRequestStatus.InProgress,
                DataRequestStatus.NotStarted,
              ],
            }, 0)
            .pipe(map((t) => t.data.totalRecords));
        }
        return of(0);
      }),
    ),
    { initialValue: 0 },
  );

  hoveredId = signal(-999);

  // Check if any nested menu is currently expanded
  hasExpandedMenu = computed(() => {
    return MENU.some(item =>
      item.children && item.children.length > 0 && this.hasActiveChild(item)
    );
  });

  hasActiveChild(item: MenuItem) {
    return item.children?.some((child) => this.router.url.startsWith(child.routerUrl ?? ''));
  }

  isActive(item: MenuItem) {
    return this.router.url.startsWith(item.routerUrl ?? '');
  }

  getBadgeValue(badgeName: string) {
    switch (badgeName) {
      case 'dataRequests':
        return this.dataRequestsCount();
      default:
        return 0;
    }
  }

  /**
   * Single visibility predicate for every menu level. Shares `RouteAccessService.isAllowed`
   * with the route guard, so the menu and the router can never disagree (a group header is
   * visible only when at least one of its children is).
   *
   * Called from the template, so it re-evaluates on a profile switch: the signal reads inside
   * `isAllowed` are tracked by this OnPush component's template consumer.
   */
  isVisible(item: MenuItem): boolean {
    if (item.children?.length) {
      return item.children.some((child) => this.isVisible(child));
    }
    return this.routeAccess.isAllowed(menuRequirement(item));
  }

  protected readonly MENU = MENU;

  expandSidebar(event: Event) {
    event.preventDefault();
    if (!this.layoutService.sidebarVisible()) {
      this.layoutService.sidebarVisible.set(true);
    }
  }
}
